import React from 'react';
import { Box, Button, alpha } from '@mui/material';
import type { OnChangeFn, VisibilityState } from '@tanstack/react-table';
import { SEARCE_COLORS } from '../../theme';
import { TABLE_STYLES } from '../../constants/tableStyles';
import TableCsvExportButton from '../table/TableCsvExportButton';
import { ColumnVisibilityMenu } from '../table/ColumnVisibilityMenu';
import type { ColumnConfig } from '../../utils/customerSentimentutils';

export interface CustomerSentimentTableToolbarProps {
  columns: ColumnConfig[];
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  onExport: () => void;
  exportDisabled?: boolean;
  showClearFilters?: boolean;
  onClearAllFilters?: () => void;
}

const CustomerSentimentTableToolbar: React.FC<CustomerSentimentTableToolbarProps> = ({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  onExport,
  exportDisabled = false,
  showClearFilters = false,
  onClearAllFilters,
}) => {
  const menuItems = columns
    .filter((c) => c.id !== 'id')
    .map((c) => ({
      id: c.id,
      label: c.label,
      hideable: true as const,
    }));

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 2.5 },
        ...TABLE_STYLES.tableToolbarTop,
        pb: 2,
        minHeight: 52,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        gap: 1.25,
        borderBottom: `1px solid ${SEARCE_COLORS.neutral_200}`,
        backgroundColor: SEARCE_COLORS.white,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
        <TableCsvExportButton disabled={exportDisabled} onClick={onExport} />
        {showClearFilters && onClearAllFilters ? (
          <Button
            variant="outlined"
            size="small"
            onClick={onClearAllFilters}
            sx={{
              color: SEARCE_COLORS.primary,
              borderColor: SEARCE_COLORS.primary,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                borderColor: SEARCE_COLORS.primary,
              },
            }}
          >
            Clear All Filters
          </Button>
        ) : null}
      </Box>
      {menuItems.length > 0 ? (
        <ColumnVisibilityMenu
          items={menuItems}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={onColumnVisibilityChange}
        />
      ) : null}
    </Box>
  );
};

export default CustomerSentimentTableToolbar;
